import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { requiredValidation } from 'validators';
import { TextField, Typography } from 'seagull';
import { InitialDiscount } from '~/components/BillModal/BillClientModal.types';
import { BillChargeClientApprovalCallout } from '~/components/BillModal/components/Callouts/Callouts';
import { getTotal } from '~/components/BillModal/utils/BillClientModal.utils';
import { ServiceTemplateInput } from '~/components/common/ServiceTemplateInput/ServiceTemplateInput';
import { DiscountTextField } from '~/components/DiscountTextField';
import { useLocaleKeys } from '~/i18n';
import { serviceCostModel, serviceTemplateModel } from '~/models';
import { costDisplayTextsPath } from '~/models/service-cost/selectors';
import { utils } from '~/utils';
import { connectControllerToTextField } from '~/utils/react-hook-form.utils';
import styles from '../../BillClientModal/BillClientModal.module.scss';
import { BillingItemNote } from '../../BillClientModal/BillingNote/BillingItemNote';
import { billClientModalTextsPath } from '../BillServiceTableItem/BillServiceTableItem.utils';
import BillTableItem from '../BillTableItem/BillTableItem';
import { getTableInfo, validateGreaterThan0 } from './BillChargeTableItem.utils';
export default function BillChargeTableItem(props) {
    const { invoiceDateInfo, actionColumn, itemIndex, chargeItem, relationship, hideApprovalCallout } = props;
    const { t: billTexts } = useLocaleKeys(billClientModalTextsPath);
    const { t: costDisplayTexts } = useLocaleKeys(costDisplayTextsPath);
    const { trigger, control, resetField, watch, setValue } = useFormContext();
    const [selectedService, setSelectedService] = useState(null);
    const [issueDate] = watch([
        'issueDate'
    ]);
    const { serviceTemplateId, quantity, rate, discount } = chargeItem.charge;
    const hasService = !!serviceTemplateId;
    const { columnDefs, headerItems } = useMemo(()=>getTableInfo({
            discount,
            tableHeaders: billTexts.tableHeaders
        }), [
        discount
    ]);
    const total = useMemo(()=>getTotal(quantity, rate, serviceTemplateId, discount), [
        quantity,
        rate,
        serviceTemplateId,
        discount
    ]);
    useEffect(()=>{
        // The error in discount field depends on total amount so we trigger change detection when total changes.
        trigger([
            "items.".concat(itemIndex, ".charge.discount")
        ]);
    }, [
        total
    ]);
    const applyServiceChange = (service)=>{
        setSelectedService(service || null);
        if (service) {
            const newQuantity = serviceTemplateModel.isFixedPrice(service.cost) ? '1' : '';
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            setValue("items.".concat(itemIndex, ".charge.quantity"), newQuantity);
            setValue("items.".concat(itemIndex, ".charge.rate"), serviceTemplateModel.price(service).abs().toString(), {
                shouldValidate: true
            });
            const discount = serviceTemplateModel.discount(service);
            const newDiscount = discount ? {
                type: discount.type(),
                amount: discount.amount().toString()
            } : InitialDiscount;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            setValue("items.".concat(itemIndex, ".charge.discount"), newDiscount);
        } else {
            resetField("items.".concat(itemIndex, ".charge.quantity"));
            resetField("items.".concat(itemIndex, ".charge.rate"));
            setValue("items.".concat(itemIndex, ".charge.discount"), InitialDiscount);
        }
    };
    const rowItems = [
        {
            value: /*#__PURE__*/ _jsx(Controller, {
                control: control,
                name: "items.".concat(itemIndex, ".charge.serviceTemplateId"),
                rules: {
                    required: true
                },
                render: (renderProps)=>/*#__PURE__*/ _jsx(ServiceTemplateInput, {
                        testId: "add-charge-".concat(itemIndex, "-service-selector"),
                        relationship: relationship,
                        menuWidth: 350,
                        renderProps: renderProps,
                        applyServiceChange: applyServiceChange
                    })
            })
        },
        {
            value: /*#__PURE__*/ _jsx(Controller, {
                control: control,
                name: "items.".concat(itemIndex, ".charge.quantity"),
                rules: {
                    validate: validateGreaterThan0
                },
                render: (renderProps)=>/*#__PURE__*/ _jsx(TextField, _object_spread({
                        bi: {
                            name: 'charge_quantity'
                        },
                        collapseHelperText: false,
                        isMoney: true,
                        size: "small",
                        testId: "add-charge-quantity-".concat(itemIndex),
                        inputProps: {
                            min: 0,
                            className: styles.amountCell
                        },
                        disabled: !hasService
                    }, connectControllerToTextField(renderProps, {
                        noHelperText: true
                    })))
            })
        },
        {
            value: /*#__PURE__*/ _jsx(Controller, {
                control: control,
                name: "items.".concat(itemIndex, ".charge.rate"),
                rules: requiredValidation(true),
                render: (renderProps)=>/*#__PURE__*/ _jsxs("div", {
                        className: styles.rate,
                        children: [
                            /*#__PURE__*/ _jsx(TextField, _object_spread({
                                bi: {
                                    name: 'charge_rate'
                                },
                                collapseHelperText: false,
                                isMoney: true,
                                size: "small",
                                testId: "add-charge-".concat(itemIndex, "-rate"),
                                inputProps: {
                                    min: 0,
                                    className: styles.amountCell
                                },
                                disabled: !hasService
                            }, connectControllerToTextField(renderProps, {
                                noHelperText: true
                            }))),
                            selectedService && serviceTemplateModel.isVariablePrice(selectedService.cost) && /*#__PURE__*/ _jsxs(Typography, {
                                ellipsis: true,
                                className: styles.units,
                                variant: "paragraph2",
                                skin: "secondary",
                                testId: "add-charge-".concat(itemIndex, "-units"),
                                children: [
                                    "/",
                                    serviceCostModel.formattedUnitName(selectedService.cost, costDisplayTexts)
                                ]
                            })
                        ]
                    })
            })
        },
        {
            value: /*#__PURE__*/ _jsx(Controller, {
                control: control,
                name: "items.".concat(itemIndex, ".charge.discount"),
                rules: {
                    validate: (value)=>value && total != null && total.lessThan(0) ? billTexts.discountCantExceedRate() : true
                },
                render: (renderProps)=>/*#__PURE__*/ _jsx("div", {
                        className: styles.discount,
                        children: /*#__PURE__*/ _jsx(DiscountTextField, _object_spread_props(_object_spread({
                            size: "small",
                            testId: "add-charge-".concat(itemIndex, "-discount"),
                            disabled: !hasService,
                            inputProps: {
                                className: styles.amountCell
                            }
                        }, connectControllerToTextField(renderProps)), {
                            value: discount,
                            onChange: (param)=>{
                                let { type, amount } = param;
                                const didTypeChange = type !== discount.type;
                                if (didTypeChange) {
                                    renderProps.field.onChange({
                                        type,
                                        amount: ''
                                    });
                                } else {
                                    renderProps.field.onChange({
                                        type,
                                        amount
                                    });
                                }
                            }
                        }))
                    })
            })
        },
        {
            value: null
        },
        {
            value: total != null ? /*#__PURE__*/ _jsx(Typography, {
                align: "right",
                testId: "add-charge-total",
                skin: total.lessThan(0) ? 'error' : 'standard',
                children: utils.formatDollarAmount(total)
            }) : null
        }
    ];
    return /*#__PURE__*/ _jsx(BillTableItem, {
        columnDefs: columnDefs,
        headerItems: headerItems,
        rowItems: rowItems,
        headerTestId: "add-charge-table-header",
        actionColumn: actionColumn,
        footer: /*#__PURE__*/ _jsxs("div", {
            className: styles.noteAndCalloutsContainer,
            children: [
                /*#__PURE__*/ _jsx(BillingItemNote, {
                    testId: "manual-bill-note-".concat(itemIndex),
                    name: "items.".concat(itemIndex, ".note")
                }),
                !hideApprovalCallout && /*#__PURE__*/ _jsx(BillChargeClientApprovalCallout, {
                    issueDate: issueDate,
                    invoiceDateInfo: invoiceDateInfo
                })
            ]
        })
    });
}
